import React from 'react';

// Simple SVG Icons
const HomeIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>;
const DocumentTextIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>;
const UsersIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M15 21a6 6 0 00-9-5.197m0 0A5.975 5.975 0 0112 13a5.975 5.975 0 013 5.197" /></svg>;
const StarIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>;

const StatCard = ({ icon, value, label, percentage, color }) => (
    <div className="bg-white p-6 rounded-lg shadow-sm flex items-start justify-between">
        <div>
            <div className={`p-2 rounded-full bg-${color}-100 text-${color}-600 inline-block`}>
                {icon}
            </div>
            <p className="text-3xl font-bold mt-4">{value}</p>
            <p className="text-gray-500">{label}</p>
        </div>
        <span className={`text-sm font-semibold text-green-500`}>{percentage}</span>
    </div>
);

const StatusBadge = ({ status }) => {
    const statusStyles = {
        Pending: 'bg-yellow-100 text-yellow-800',
        Approved: 'bg-green-100 text-green-800',
        Rejected: 'bg-red-100 text-red-800',
        Active: 'bg-green-100 text-green-800',
        Inactive: 'bg-gray-100 text-gray-800',
    };
    return (
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusStyles[status]}`}>
            {status}
        </span>
    );
};

const DashboardContent = () => {
    // ✅ Initial stats set to 0
    const stats = {
        activeUsers: 0,
        totalRevenue: 0,
        pendingRequests: 0,
        newUsers: 0,
    };

    // ✅ Empty arrays for tables
    const mentorshipRequests = [];
    const assignedStudents = [];
    const scheduledSessions = [];

    return (
        <div className="flex-1 bg-gray-50 p-8">

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <StatCard icon={<UsersIcon />} value={stats.activeUsers} label="Active Users" percentage="0%" color="indigo" />
                <StatCard icon={<p className="font-bold text-xl">$</p>} value={`$${stats.totalRevenue}`} label="Total Revenue" percentage="0%" color="blue" />
                <StatCard icon={<DocumentTextIcon />} value={stats.pendingRequests} label="Pending Requests" percentage="0%" color="purple" />
                <StatCard icon={<StarIcon />} value={stats.newUsers} label="New Users" percentage="0%" color="pink" />
            </div>

            {/* Tables */}
            <div className="space-y-8">
                {/* Mentorship Requests */}
                <div className="bg-white p-6 rounded-lg shadow-sm">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-semibold text-gray-800">Mentorship Requests</h3>
                        <a href="#" className="text-sm text-indigo-600 hover:underline">View All</a>
                    </div>
                    {mentorshipRequests.length === 0 ? (
                        <p className="text-gray-500 text-sm">No mentorship requests yet.</p>
                    ) : (
                        <table className="w-full text-left">
                            <thead>
                                <tr className="text-gray-500 text-sm">
                                    <th className="py-2">Request ID</th>
                                    <th>Student Name</th>
                                    <th>Type</th>
                                    <th>Date</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mentorshipRequests.map(req => (
                                    <tr key={req.id} className="border-t border-gray-200">
                                        <td className="py-4 text-gray-700">{req.id}</td>
                                        <td className="text-gray-900 font-medium">{req.student}</td>
                                        <td className="text-gray-700">{req.type}</td>
                                        <td className="text-gray-700">{req.date}</td>
                                        <td><StatusBadge status={req.status} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Assigned Students */}
                <div className="bg-white p-6 rounded-lg shadow-sm">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-semibold text-gray-800">Assigned Students</h3>
                        <a href="#" className="text-sm text-indigo-600 hover:underline">Show More</a>
                    </div>
                    {assignedStudents.length === 0 ? (
                        <p className="text-gray-500 text-sm">No assigned students yet.</p>
                    ) : (
                        <table className="w-full text-left">
                            <thead>
                                <tr className="text-gray-500 text-sm">
                                    <th className="py-2">User ID</th>
                                    <th>Name</th>
                                    <th>Role</th>
                                    <th>Email</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {assignedStudents.map(student => (
                                    <tr key={student.id} className="border-t border-gray-200">
                                        <td className="py-4 text-gray-700">{student.id}</td>
                                        <td className="text-gray-900 font-medium">{student.name}</td>
                                        <td className="text-gray-700">{student.role}</td>
                                        <td className="text-gray-700">{student.email}</td>
                                        <td><StatusBadge status={student.status} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Scheduled Sessions */}
                <div className="bg-white p-6 rounded-lg shadow-sm">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-semibold text-gray-800">Scheduled Sessions</h3>
                        <a href="#" className="text-sm text-indigo-600 hover:underline">View Calendar</a>
                    </div>
                    {scheduledSessions.length === 0 ? (
                        <p className="text-gray-500 text-sm">No scheduled sessions yet.</p>
                    ) : (
                        <table className="w-full text-left">
                            <thead>
                                <tr className="text-gray-500 text-sm">
                                    <th className="py-2">Event Name</th>
                                    <th>Date</th>
                                    <th>Host</th>
                                    <th>Description</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {scheduledSessions.map(session => (
                                    <tr key={session.name} className="border-t border-gray-200">
                                        <td className="py-4 text-gray-900 font-medium">{session.name}</td>
                                        <td className="text-gray-700">{session.date}</td>
                                        <td className="text-gray-700">{session.host}</td>
                                        <td className="text-gray-700">{session.desc}</td>
                                        <td className="text-gray-500 cursor-pointer">...</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

// Main App Component
const DashboardPage = () => {
    return (
        <div className="min-h-screen flex bg-gray-50">
            <DashboardContent />
        </div>
    );
};

export default DashboardPage;
